'use strict';

import { Entity } from '../Entity';
import { Utilities } from '../../Utilities/Utilities';
import type { CommissionStatementModuleName } from '../../Model/CommissionStatement/CommissionStatementModule';
import { CommissionStatement } from './CommissionStatement';
import type { SelectMap } from '../../../MedicareProFront/Utilities/SelectMap';
import { SelectMapGroup } from '../../../MedicareProFront/Utilities/SelectMap';

export enum CommissionStatementResultSummaryType {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
}

export enum CommissionStatementResultSummaryGroup {
  IND_POLICY_COMP_HISTORY_INSERT = 'ind_policy_comp_history_insert',
  IND_POLICY_COMP_HISTORY_UPDATE = 'ind_policy_comp_history_update',
  IND_POLICY_NUMBER_UPDATE = 'ind_policy_number_update',
}

export type CommissionStatementResultSummary = {
  type?: CommissionStatementResultSummaryType;
  group?: CommissionStatementResultSummaryGroup;
  description: string & { readonly __brand: 'HtmlString' };
  detail: Array<SelectMap>;
};

/**
 * Entity representing a Commission Statement processing result
 * Contains data from commission statement processing operations and their associated metadata
 */
export class CommissionStatementResult extends Entity {
  protected id: number;
  protected data: any;
  protected commissionStatement: CommissionStatement;
  protected module: CommissionStatementModuleName;
  protected dateTimeCreated: Date;
  protected summary: Array<CommissionStatementResultSummary>;

  constructor() {
    super();
  }

  /**
   * Hydrates the entity with raw data from API response
   * @param data - Raw data object containing commission statement result properties
   */
  public hydrate(data: any): void {
    this.id = data.id;
    this.module = data.module;
    this.data = data.data;
    this.summary = data.summary;

    if (data.commissionStatement) {
      const commissionStatement = new CommissionStatement();
      commissionStatement.hydrate(data.commissionStatement);
      this.commissionStatement = commissionStatement;
    }

    // Convert date string to Date object
    if (data.dateTimeCreated) {
      const dateTimeCreated = Utilities.dateTimeStringToDateTime(data.dateTimeCreated);
      this.dateTimeCreated = dateTimeCreated;
    }
  }

  /**
   * Gets the unique identifier for this commission statement result
   * @returns The numeric ID of this result
   */
  public getId(): number {
    return this.id;
  }

  /**
   * Sets the unique identifier for this commission statement result
   * @param id - The numeric ID to set
   */
  public setId(id: number): void {
    this.id = id;
  }

  /**
   * Gets the processing data/results stored in this commission statement result
   * @returns The data object containing processing results
   */
  public getData(): any {
    return this.data;
  }

  /**
   * Sets the processing data/results for this commission statement result
   * @param data - The data object to store as processing results
   */
  public setData(data: any): void {
    this.data = data;
  }

  public getSummary(): Array<CommissionStatementResultSummary> {
    return this.summary;
  }

  public setSummary(summary: Array<CommissionStatementResultSummary>): void {
    this.summary = summary;
  }

  /**
   * Gets the commission statement entity associated with this result
   * @returns The CommissionStatement entity this result belongs to
   */
  public getCommissionStatement(): CommissionStatement {
    return this.commissionStatement;
  }

  /**
   * Sets the commission statement entity associated with this result
   * @param commissionStatement - The CommissionStatement entity to associate
   */
  public setCommissionStatement(commissionStatement: CommissionStatement): void {
    this.commissionStatement = commissionStatement;
  }

  /**
   * Gets the module name that processed this commission statement result
   * @returns The module name that generated this result
   */
  public getModule(): CommissionStatementModuleName {
    return this.module;
  }

  /**
   * Sets the module name that processed this commission statement result
   * @param module - The module name to set
   */
  public setModule(module: CommissionStatementModuleName): void {
    this.module = module;
  }

  /**
   * Gets the date and time when this commission statement result was created
   * @returns The creation date and time as a Date object
   */
  public getDateTimeCreated(): Date {
    return this.dateTimeCreated;
  }

  /**
   * Sets the date and time when this commission statement result was created
   * @param dateTimeCreated - The creation date and time to set
   */
  public setDateTimeCreated(dateTimeCreated: Date): void {
    this.dateTimeCreated = dateTimeCreated;
  }
}
